import React from "react";
import { ArrowLeft } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { tenantHref } from "@/lib/tenantPath";
// Relative on purpose: pure modules and components, resolvable under Jest without a shim.
import { CATALOGUE_PATH, isFlatPath, isJoinable } from "../../pathways/learnerModel";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";
import JoinForm from "./JoinForm";
import PathwayCard from "./PathwayCard";

/**
 * `/parcours/:pathwayId` for a visitor who has not joined (contract §9): the card detail,
 * the join form, then the outline, titles only (contract §5.2: a public shape never carries
 * step content). The form follows the card as on the listing wireframe, so on a phone the
 * way in is right under the price.
 *
 * The outline is grouped by level with "Level 1 · Basics" headers for the level type; a
 * successive or alternative pathway is one flat list (its single level's title is not shown
 * to learners, contract §2).
 *
 * @param {object} props
 * @param {object} props.detail   PublicPathwayDetail
 * @param {object} props.auth     useRequireAuth() result
 * @param {(pathwayId: string) => void} props.onEnrolled
 * @param {() => void} props.onFull  the figures are stale: re-read the detail
 */
export default function PathwayDetail({ detail, auth, onEnrolled, onFull }) {
  const t = useT("public");
  const flat = isFlatPath(detail.path_type);

  return (
    <div data-testid={IDS.detail} className="mx-auto w-full max-w-xl space-y-5">
      <a href={tenantHref(CATALOGUE_PATH)} className="inline-flex items-center gap-2 text-sm text-eh-muted hover:text-eh-ink">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {t("pathways.detail.back")}
      </a>

      <PathwayCard card={detail} as="h1" full />

      {isJoinable(detail) && <JoinForm card={detail} auth={auth} onEnrolled={onEnrolled} onFull={onFull} />}

      {detail.levels.some((l) => l.steps.length > 0) && (
        <section data-testid={IDS.detailOutline} aria-labelledby="pathway-outline" className="rounded-3xl border border-eh-border-soft bg-eh-cream p-5 sm:p-6">
          <h2 id="pathway-outline" className="font-medium text-eh-ink">{t("pathways.detail.outline")}</h2>
          <div className="mt-4 space-y-4">
            {detail.levels.map((level, i) => (
              <div key={level.id} data-testid={IDS.detailLevel(level.id)}>
                {!flat && (
                  <h3 className="mb-2 text-sm font-semibold text-eh-ink">
                    {t("pathways.levelHeading", { n: i + 1, title: level.title })}
                  </h3>
                )}
                <ol className="space-y-1.5">
                  {level.steps.map((step) => (
                    <li key={step.id} data-testid={IDS.detailStep(step.id)} className="flex items-center gap-3 rounded-2xl bg-eh-surface px-4 py-2.5 text-sm text-eh-ink">
                      <span className="min-w-0 flex-1 break-words">{step.title}</span>
                      {!step.required && <span className="flex-shrink-0 text-xs text-eh-muted">{t("pathways.home.optional")}</span>}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
